import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Store } from "../../redux/store"
import { appError } from "../../redux/slices/app"
import { mobileDevice } from "../../utils/constants/browser"
import { PortalAccountDomain, ScreenSize } from "../../utils/enums"
import { useAppState, useEnsureCountries } from "../../utils/hooks"
import r from "../../utils/routes"
import api from "../../utils/api"
import Utils from "../../utils/utils"
import { Question } from "../../utils/types"
import { setQuestionnaire } from "../redux/state"
import { captureIBError } from "./utils"

export const useIBState = () => {
    return useSelector((state: Store) => state.IB)
}

export function useIBQuestionsFetch(onLoaded?: (q: Question[]) => void) {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { userProfile, organizations } = useAppState()
    const { questionnaire } = useIBState()
    const [ready, setReady] = useState(false)
    const [loadFailed, setLoadFailed] = useState(false)
    const fetched = useRef(false)
    const onLoadedRef = useRef(onLoaded)

    // `organizations` is derived from countries, which are deferred out of cold
    // boot (3.12.16). The effect below stays in its !organizations early-return
    // (ready never flips) until they load, so ensure the fetch here rather than
    // relying on every consuming step to remember useEnsureCountries().
    useEnsureCountries()

    useEffect(() => {
        onLoadedRef.current = onLoaded
    })

    // IB questionnaire is TMLC-only; other org ids return a different
    // question set and break Q1<->Q3 routing.
    const tmlcOrgId = useMemo(
        () => organizations?.find(o => o.name === PortalAccountDomain.TMLC)?.id,
        [organizations]
    )

    useEffect(() => {
        if (fetched.current) return
        if (questionnaire?.length) {
            fetched.current = true
            setReady(true)
            onLoadedRef.current?.(questionnaire)
            return
        }
        // Wait until organizations have been loaded by initCountries.
        if (!organizations?.length) return
        // Loaded but TMLC not present — surface an error rather than spin forever.
        if (!tmlcOrgId) {
            fetched.current = true
            captureIBError('TMLC org id not found in organizations list', userProfile?.organization?.id)
            dispatch(appError(Utils.customError(t('common:errors.generic'))))
            setLoadFailed(true)
            setReady(true)
            return
        }
        fetched.current = true
        const fetch = async () => {
            try {
                const response = await api.getIBQuestions(tmlcOrgId)
                const q = response.payload?.[0]?.result
                if (!q?.length) {
                    captureIBError('IB questionnaire empty', tmlcOrgId)
                    dispatch(appError(Utils.customError(t('common:errors.generic'))))
                    setLoadFailed(true)
                    return
                }
                dispatch(setQuestionnaire(q))
                onLoadedRef.current?.(q)
            }
            catch (error) {
                console.error(error)
                dispatch(appError(Utils.customError(
                    error instanceof Error ? error.message : 'Failed to load questionnaire'
                )))
                setLoadFailed(true)
            }
            finally { setReady(true) }
        }
        fetch()
    }, [tmlcOrgId, questionnaire, organizations, dispatch, t, userProfile?.organization?.id])

    return { ready, loadFailed }
}

export function usePagination<T>(data: T[], pageSize: number, total?: number) {
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState<number>(pageSize)
    const length = total ? total : data.length
    const maxPage = Math.ceil(length / itemsPerPage)

    const currentData = useMemo(() => {
        const begin = (currentPage - 1) * itemsPerPage
        const end = begin + itemsPerPage
        return data.slice(begin, end)
    }, [currentPage, data, itemsPerPage])

    const next = useCallback(() => {
        setCurrentPage((currentPage) => Math.min(currentPage + 1, maxPage))
    }, [maxPage])

    const prev = useCallback(() => {
        setCurrentPage((currentPage) => Math.max(currentPage - 1, 1))
    }, [])

    const jump = useCallback((page: number) => {
        const pageNumber = Math.max(1, page)
        const newPage = Math.min(pageNumber, maxPage)
        setCurrentPage(() => Math.max(1, newPage))
    }, [maxPage])

    const setPerPageNumber = useCallback((amount: number | null) => {
        const value = amount === null ? length : amount
        setItemsPerPage(value)
        setCurrentPage(1)
    }, [length])

    return { next, prev, jump, setPerPageNumber, setCurrentPage, currentPage, maxPage, itemsPerPage, data, currentData }
}

export const useIBScreen = () => {
    const { screen } = useIBState()
    const notDesktop = useMemo(() => mobileDevice || screen !== ScreenSize.Desktop, [screen])
    const isDesktop = useMemo(() => !mobileDevice && screen === ScreenSize.Desktop, [screen])
    const isMobile = useMemo(() => mobileDevice || screen === ScreenSize.Mobile, [screen])
    const isTablet = useMemo(() => !mobileDevice && screen === ScreenSize.Tablet, [screen])

    return {
        notDesktop,
        isDesktop,
        isMobile,
        isTablet
    }
}

export const useShouldShowIBportal = () => {
    const { application: ibApplication } = useIBState()
    const { liveAccounts } = useAppState()
    const hasComissionAcc = liveAccounts.some(x => x.platformAccountType === 'Commission')
    return (hasComissionAcc || !!ibApplication)
}

export const useBackButton = () => {
    const { userInsidePortal } = useAppState()
    const { questionnaire } = useIBState()
    const history = useHistory()
    const location = useLocation()

    const hasIBQ6 = useMemo(() =>
        questionnaire?.some(q => q.label === 'IB Q6') ?? false
    , [questionnaire])

    const goBack = useCallback(() => {
        switch (location.pathname) {
            case r.account.ib.q1:
                history.push(r.account.ib.phone)
                break
            case r.account.ib.q2:
                history.push(r.account.ib.q1)
                break
            case r.account.ib.q3:
                history.push(r.account.ib.q2)
                break
            case r.account.ib.q4:
                history.push(r.account.ib.q3)
                break
            case r.account.ib.legal:
                history.push(hasIBQ6 ? r.account.ib.q4 : r.account.ib.q2)
                break
            case r.account.ib.verify.address:
                history.push(r.account.ib.verify.id)
                break
            case r.account.ib.verify.id:
                history.push(r.account.ib.address)
                break
            case r.account.ib.address:
                if (userInsidePortal) history.push(r.ib.dashboard)
                break
            default:
        }
    }, [history, location, userInsidePortal, hasIBQ6])

    return goBack
}

export const useIsMasterIb = () => {
    const { selectedPartner } = useIBState()
    const [isMasterIb, setIsMasterIb] = useState(false)

    useEffect(() => {
        let cancelled = false
        setIsMasterIb(false)
        const checkIsMaster = async () => {
            if (!selectedPartner?.id) return
            try {
                const response = await api.getMasterIBSummary({ partnerId: selectedPartner.id })
                if (!cancelled && Utils.validResponse(response)) {
                    setIsMasterIb(response.payload[0].result.totalSubIbs > 0)
                }
            } catch (e) {
                // Background eligibility check: on error, isMasterIb stays false.
                // Do not dispatch appError — this should not block the IB portal.
                console.error('[useIsMasterIb] Failed to determine Master IB status', e)
            }
        }
        checkIsMaster()
        return () => { cancelled = true }
    }, [selectedPartner?.id])

    return isMasterIb
}
